import UserGuide from '@/components/help/UserGuide';
import { Input, Select } from '@/components/ui/FormControls';
import { useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CheckCheck, ChevronDown, Search, ShieldAlert } from 'lucide-react';
import { ACCESS_PRESETS, PERMISSION_CATALOG, inspectPermissions, roleLabel } from '@klothiq/domain';
import { accessActionClass, accessInputClass } from './user-access-styles';
import { useAuth } from '@/contexts/AuthContext';

function moduleLabel(module: string) {
  const labels: Record<string, string> = {
    my_tasks: 'My tasks',
    plans: 'Plans & tasks',
    master_config: 'Master configuration',
    recycle: 'Recycle bin',
    imports: 'Data imports',
    financials: 'Financial information',
    audit: 'Audit history',
  };
  if (labels[module]) return labels[module];
  const name = module.startsWith('report_')
    ? `${module.slice(7).replaceAll('_', ' ')} report`
    : module.replaceAll('_', ' ');
  return name.charAt(0).toUpperCase() + name.slice(1);
}

interface PermissionPickerProps {
  role: 'admin' | 'staff';
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

export function PermissionPicker({ role, values, onChange, disabled }: PermissionPickerProps) {
  const { access } = useAuth();
  const [search, setSearch] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const cancelButton = useRef<HTMLButtonElement>(null);
  const available = useMemo(
    () =>
      PERMISSION_CATALOG.filter(
        (permission) =>
          permission.minimumRole !== 'owner' &&
          (![
            'purchases.correct',
            'purchases.cancel',
            'purchases.status',
            'purchases.delete',
            'purchases.restore',
            'purchases.archive',
          ].includes(permission.key) ||
            access?.permissions.includes(permission.key)) &&
          inspectPermissions([permission.key], role).issues.length === 0,
      ),
    [role, access?.permissions],
  );
  const inspected = inspectPermissions(values, role);
  const resolved = new Set(inspected.permissions);
  const hasInvalidGrants = inspected.issues.length > 0;
  const displayed = PERMISSION_CATALOG.filter(
    (permission) =>
      permission.module !== 'lookup' &&
      (available.includes(permission) || permission.key === 'purchases.view'),
  );
  const selectable = available.filter((permission) => permission.module !== 'lookup');
  const selectedCount = displayed.filter((permission) => resolved.has(permission.key)).length;
  const allSelected = available.every((permission) => resolved.has(permission.key));
  const applySelection = (next: string[]) => {
    if (disabled) return;
    const nextInspection = inspectPermissions(next, role);
    const issue = nextInspection.issues[0];
    if (issue) {
      setSelectionError(issue.message);
      return;
    }
    setSelectionError('');
    onChange(next);
  };
  const query = search.trim().toLowerCase();
  const groups = [...new Set(displayed.map((permission) => permission.module))]
    .map((module) => {
      const entries = displayed.filter((permission) => permission.module === module);
      return {
        module,
        title: moduleLabel(module),
        entries,
        matches: entries.filter((permission) =>
          `${moduleLabel(module)} ${permission.label} ${permission.key}`
            .toLowerCase()
            .includes(query),
        ),
      };
    })
    .filter((group) => group.matches.length > 0);

  return (
    <section className="space-y-4" aria-label="Screen and action privileges">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Screen & action privileges</h3>
        <span
          role="status"
          className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
        >
          {selectedCount} / {selectable.length} selected
        </span>
      </div>

      {hasInvalidGrants && (
        <div
          role="alert"
          className="space-y-3 rounded-xl border border-destructive/25 bg-destructive/5 p-3 text-sm"
        >
          <p className="font-medium">Some privileges are unavailable for this role.</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Review these entries before saving. Removing them here changes this draft only; saved
            access changes when you submit the account form.
          </p>
          <ul className="space-y-3">
            {inspected.issues.map((issue) => (
              <li key={issue.key} className="space-y-2">
                <p className="break-words">
                  {PERMISSION_CATALOG.find((p) => p.key === issue.key)?.label ?? issue.key}
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">{issue.message}</p>
                <button
                  type="button"
                  disabled={disabled}
                  className={accessActionClass}
                  onClick={() => {
                    setSelectionError('');
                    onChange(values.filter((key) => key !== issue.key));
                  }}
                >
                  Remove from this draft
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {selectionError && (
        <p role="alert" className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
          {selectionError}
        </p>
      )}

      <Dialog.Root open={confirmAll} onOpenChange={setConfirmAll}>
        <Dialog.Trigger asChild>
          <button
            type="button"
            disabled={disabled || hasInvalidGrants || allSelected}
            className={`${accessActionClass} w-full border-primary/25 bg-primary/5 text-primary hover:bg-primary/10`}
          >
            <CheckCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
            {allSelected ? 'All privileges selected' : 'Select all privileges'}
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" data-field-guide-scope />
          <Dialog.Content
            className="obsidian-panel fixed left-1/2 top-1/2 z-[60] flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              cancelButton.current?.focus();
            }}
          >
            <div className="min-h-0 space-y-4 overflow-y-auto p-5 sm:p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                <ShieldAlert className="h-6 w-6" aria-hidden="true" />
              </div>
              <div className="flex items-start justify-between gap-3 pr-8">
                <Dialog.Title className="text-lg font-semibold">
                  Select every {roleLabel(role)} privilege?
                </Dialog.Title>
                <UserGuide compact />
              </div>
              <Dialog.Description className="text-sm leading-relaxed text-muted-foreground">
                This selects all {selectable.length} screen and action privileges available to the{' '}
                {roleLabel(role)} role. Your changes take effect when you save the account form.
              </Dialog.Description>
              <p className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-sm leading-relaxed">
                {role === 'admin'
                  ? 'Includes confidential financial information, payment changes and permanent deletion of eligible records.'
                  : 'Includes sales, payment collection and corrections, customer and supplier actions, and assigned work.'}
              </p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                User and access administration remains restricted to the Super Admin. You can adjust
                individual selections afterward.
              </p>
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-border p-4">
              <Dialog.Close asChild>
                <button ref={cancelButton} type="button" className={accessActionClass}>
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={disabled || hasInvalidGrants}
                className={`${accessActionClass} border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
                onClick={() => {
                  if (disabled || hasInvalidGrants) return;
                  applySelection(available.map((permission) => permission.key));
                  setConfirmAll(false);
                }}
              >
                Select all
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <label className="block space-y-2 text-xs font-medium text-muted-foreground">
        Quick setup
        <Select
          value=""
          disabled={disabled || hasInvalidGrants}
          className={`${accessInputClass} text-foreground`}
          onChange={(event) => {
            const preset = ACCESS_PRESETS.find((item) => item.id === event.target.value);
            if (preset) applySelection([...preset.permissions]);
          }}
          helpLabel="Quick setup"
        >
          <option value="" disabled>
            Choose a preset
          </option>
          {ACCESS_PRESETS.filter(
            (preset) => inspectPermissions(preset.permissions, role).issues.length === 0,
          ).map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.name}
            </option>
          ))}
        </Select>
      </label>

      <label className="relative block">
        <span className="sr-only">Find a privilege</span>
        <Search
          className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          className={`${accessInputClass} pl-10`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
          placeholder="Find a screen or action…"
          helpLabel="Find a privilege"
        />
      </label>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Open a section to fine-tune access. Required form lookups are included automatically.
      </p>
      {groups.length ? (
        <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
          {groups.map(({ module, title, entries, matches }) => (
            <details key={module} className="group" open={query ? true : module === 'my_tasks'}>
              <summary className="flex min-h-12 cursor-pointer list-none items-center gap-3 px-3 py-3 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary [&::-webkit-details-marker]:hidden sm:px-4">
                <ChevronDown
                  className="h-4 w-4 shrink-0 -rotate-90 text-muted-foreground transition-transform group-open:rotate-0"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">{title}</span>
                <span className="rounded-md bg-muted px-2 py-1 text-xs tabular-nums text-muted-foreground">
                  {entries.some((permission) => available.includes(permission))
                    ? `${entries.filter((permission) => resolved.has(permission.key)).length}/${entries.filter((permission) => available.includes(permission)).length}`
                    : 'Admin only'}
                </span>
              </summary>
              <div className="grid gap-2 px-3 pb-3 sm:grid-cols-2 sm:px-4 sm:pb-4">
                {matches.map((permission) => {
                  const selected = resolved.has(permission.key);
                  const inherited = selected && !values.includes(permission.key);
                  const unavailable = !available.includes(permission);
                  return (
                    <label
                      key={permission.key}
                      className={`flex min-h-12 items-start gap-3 rounded-xl border p-3 text-sm leading-snug ${selected ? 'border-primary/15 bg-primary/5' : 'border-transparent bg-muted/30'} ${disabled || inherited || unavailable || hasInvalidGrants ? '' : 'cursor-pointer'}`}
                    >
                      <Input
                        type="checkbox"
                        className="mt-0.5 h-5 w-5 shrink-0 accent-primary focus-visible:outline-primary"
                        checked={selected}
                        disabled={disabled || inherited || unavailable || hasInvalidGrants}
                        onChange={(event) =>
                          applySelection(
                            event.target.checked
                              ? [...values, permission.key]
                              : values.filter((key) => key !== permission.key),
                          )
                        }
                        helpLabel={permission.label}
                        help={`${permission.label}. ${inherited ? 'Required by another selected permission; remove that permission first if this access is no longer needed.' : unavailable ? 'This permission is unavailable for the selected role.' : 'Grant this action only when the person needs it for their work.'}`}
                      />
                      <span className="min-w-0 flex-1 break-words">
                        {permission.label}
                        {unavailable && (
                          <small className="mt-1 block text-xs leading-normal text-muted-foreground">
                            {permission.key === 'purchases.view'
                              ? 'Requires Admin access because purchases include confidential financial information.'
                              : 'Unavailable for this role.'}
                          </small>
                        )}
                        {inherited && (
                          <small className="mt-1 block text-xs leading-normal text-muted-foreground">
                            Required by another selected privilege
                          </small>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </details>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground">
          <p>No privileges match “{search}”.</p>
          <button
            type="button"
            className={`${accessActionClass} mt-3`}
            onClick={() => setSearch('')}
          >
            Clear search
          </button>
        </div>
      )}
    </section>
  );
}
